import { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import { useAppSelector } from '../../../../store/store';
import {
  OutgoingCallsRequest,
  outgoingCallsStatus,
  selectOutgoingCalls,
  totalOutgoingCalls,
} from '../../../../store/reducers/OutgoingCallsSlice';
import { selectClinics } from '../../../../store/reducers/ClinicSlice';
import { selectSpecializations } from '../../../../store/reducers/SpecializationDoctorSlice';
import { selectSurnameDoctor } from '../../../../store/reducers/surnameDoctor';
import { ReportRequest } from '../../../../store/reducers/ReportSlice';
import { phone } from '../../../../utils/phone';
import { formatDateTime } from '../../../../utils';
import { CurrentOutgoingCalls } from '../../../../models/OutgoingCalls/OutgoingCalls';
import { BlockNumberItem } from '../../../../models/BlockNumbers/BlockNumbers';
import { formatOutgoingDoctorInfo } from './outgoingDoctorInfo';
import { CallAttempt } from '../../../../models/CallAttempt/CallAttempt';

const PAGE_SIZE: number = 20;

const getLatestAttempt = (callAttempts?: CallAttempt[] | null) => {
  if (!callAttempts?.length) return undefined;

  return callAttempts.reduce((latest, current) => {
    const latestTime = new Date(
      latest.callDateTime ?? latest.beginCallDateTime ?? '',
    ).getTime();
    const currentTime = new Date(
      current.callDateTime ?? current.beginCallDateTime ?? '',
    ).getTime();

    return currentTime > latestTime ? current : latest;
  });
};

const getLatestAttemptState = (
  callAttempts?: CallAttempt[] | null,
  fallbackState?: string | null,
) => {
  const latestAttempt = getLatestAttempt(callAttempts);

  return (
    latestAttempt?.callAttemptState ??
    latestAttempt?.state ??
    fallbackState ??
    undefined
  );
};

const getOutgoingCallDateTime = (call: {
  state?: string;
  expectedCallDateTime?: string;
  callDateTime?: string;
  callAttempts?: CallAttempt[] | null;
}) => {
  if (call.state === 'Processed') {
    const latestAttempt = getLatestAttempt(call.callAttempts);
    return (
      latestAttempt?.callDateTime ??
      latestAttempt?.beginCallDateTime ??
      call.expectedCallDateTime ??
      call.callDateTime ??
      ''
    );
  }

  return call.expectedCallDateTime ?? call.callDateTime ?? '';
};

export const useOutgoingCallsState = () => {
  const outgoingCalls = useAppSelector(selectOutgoingCalls);
  const clinics = useAppSelector(selectClinics);
  const specializations = useAppSelector(selectSpecializations);
  const surnameDoctors = useAppSelector(selectSurnameDoctor);
  const totalCount = useAppSelector(totalOutgoingCalls);
  const status = useAppSelector(outgoingCallsStatus);
  const [index, setIndex] = useState<number>();
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [callRequest, setCallRequest] = useState<OutgoingCallsRequest>({
    'Filter.From': dayjs().format('YYYY-MM-DD'),
    'Filter.To': dayjs().format('YYYY-MM-DD'),
    PageIndex: 0,
    PageSize: pageSize,
    SortKeySelector: 'state',
    SortOrder: 'Asc',
    'Filter.ClinicIds': [],
    'Filter.SpecializationIds': [],
    'Filter.DoctorIds': [],
    'Filter.States': [],
    'Filter.AttemptStates': [],
    'Filter.Result': [],
    'Filter.NotificationTypes': [],
    'Filter.TextSearch': '',
  } as OutgoingCallsRequest);

  const [callRequestReport, setCallRequestReport] = useState<ReportRequest>({
    From: dayjs().format('YYYY-MM-DD'),
    To: dayjs().format('YYYY-MM-DD'),
  });

  const totalBlockCount = 0;

  let resultBlock: BlockNumberItem[] = [];

  const currentResultBlock: CurrentOutgoingCalls[] = useMemo(() => {
    if (!resultBlock) return [];
    return resultBlock.map((call: BlockNumberItem, index: number) => ({
      id: call.id,
      phone: phone(call.phone),
      surname: call.fullName,
      date: formatDateTime(call.created),
      clinic: '',
      surnameDoctor: '',
      status: '',
      indexId: index + totalCount,
    }));
  }, [resultBlock, totalCount]);
  let total = totalCount + totalBlockCount;

  const currentOutgoingCalls: CurrentOutgoingCalls[] = useMemo(() => {
    if (!outgoingCalls) return [];

    return outgoingCalls.map((call, index) => {
      const specialization = `${call.specialization?.title ?? ''}`;

      const doctorParts = [
        call.doctor?.surname ?? '',
        call.doctor?.givenName?.[0] ? `${call.doctor.givenName[0]}.` : '',
        call.doctor?.patronym?.[0] ? `${call.doctor.patronym[0]}.` : '',
      ].filter((p): p is string => p.length > 0);

      const doctorName = doctorParts.join(' ');

      const surnameDoctor = formatOutgoingDoctorInfo(
        doctorName,
        specialization,
      );

      const surname = [call.surname, call.givenName, call.patronym]
        .filter((p): p is string => Boolean(p))
        .join(' ');

      return {
        id: call.id,
        phone: phone(call.phone),
        surname,
        date: formatDateTime(getOutgoingCallDateTime(call)),
        appointmentDate: formatDateTime(call.begin ?? ''),
        clinic: call.clinic?.name ?? '',
        surnameDoctor,
        notificationType: call.notificationType,
        type: call.type,
        status: call.state ?? '',
        result: call.result,
        attemptState: getLatestAttemptState(call.callAttempts, call.state),
        indexId: index,
      };
    });
  }, [outgoingCalls]);

  const allCalls: CurrentOutgoingCalls[] = useMemo(
    () => [...currentOutgoingCalls, ...currentResultBlock],
    [currentOutgoingCalls, currentResultBlock],
  );

  return {
    clinics,
    specializations,
    surnameDoctors,
    totalCount,
    status,
    setIndex,
    pageSize,
    setPageSize,
    callRequest,
    setCallRequest,
    callRequestReport,
    setCallRequestReport,
    total,
    currentOutgoingCalls,
    allCalls,
  };
};
